import { observer } from 'mobx-react-lite'
import React, { useCallback, useMemo } from 'react'
import { StyleSheet } from 'react-native'
import { Avatar } from 'react-native-paper'

import { BottomSheetBackdrop, BottomSheetModal } from '@gorhom/bottom-sheet'

import { DS } from '../../design-system'
import { color, fontFamily } from '../../theme'
import { GradientButton } from '../GradientButton/GradientButton'
import Illustrations from './assets/Illustrations.svg'

import type { LatestResponsesModalType } from '../../models/session/session-daily'
import type { RefObject } from 'react'
import { ReduceMotion } from 'react-native-reanimated'
const USER_BADGE_COLOR = [color.palette.orange, color.palette.blue]

type DailyInspirationRecentResponseProps = {
  bottomSheetModalRef: RefObject<BottomSheetModal | null>
  onClose: () => void
  onPressViewResponse: () => void
  recentResponses: LatestResponsesModalType[]
  bottomSheetHeight: string
}

const CardView = ({
  name,
  message,
  avatar,
  avatarColor
}: {
  name: string
  message: string
  avatar: string
  avatarColor: string
}) => {
  return (
    <DS.Frame bg="Global/White" style={styles.cardContainer}>
      <DS.Frame row gap="12">
        <Avatar.Text
          size={32}
          label={name[0]?.toUpperCase() || 'U'}
          style={{ backgroundColor: avatarColor, marginRight: 8 }}
          color={color.palette.black}
          labelStyle={{ fontFamily: fontFamily.GTAmericaExtended, fontSize: 12 }}
        />

        <DS.Frame style={{ flex: 1 }}>
          <DS.Text variant="Body Small/Medium">{name}</DS.Text>
          <DS.Text variant="Body Small/Regular" numberOfLines={3}>
            {message}
          </DS.Text>
        </DS.Frame>
      </DS.Frame>
    </DS.Frame>
  )
}

export const DailyInspirationRecentResponseSheet = observer(
  ({
    bottomSheetModalRef,
    onClose,
    onPressViewResponse,
    recentResponses,
    bottomSheetHeight
  }: DailyInspirationRecentResponseProps) => {
    const renderBackdrop = useCallback(
      (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
        <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} />
      ),
      []
    )
    const snapPoints = useMemo(() => [bottomSheetHeight], [bottomSheetHeight])

    return (
      <DS.Frame>
        <BottomSheetModal
          ref={bottomSheetModalRef}
          index={0}
          snapPoints={snapPoints}
          backdropComponent={renderBackdrop}
          detached={false}
          handleStyle={styles.bottomSheetHandleStyle}
          handleIndicatorStyle={styles.bottomSheetIndicatorStyle}
          keyboardBlurBehavior="restore"
          backgroundStyle={{ backgroundColor: color.palette.cream }}
          overrideReduceMotion={ReduceMotion.System}
        >
          <DS.Frame bg="Brand/Cream" style={{ paddingHorizontal: 24 }}>
            <DS.Frame style={styles.messageTopContainer}>
              <DS.Frame style={{ marginTop: 10 }}>
                <Illustrations />
              </DS.Frame>
              <DS.Text variant="Heading 3/Medium" style={{ marginVertical: 8 }}>
                Thank you for sharing!
              </DS.Text>
              <DS.Text variant="Body Small/Regular" textAlign="center" style={styles.discription}>
                {recentResponses.length > 0
                  ? 'Explore thoughtful insights from the community and show your support.'
                  : 'You’ve started the conversation! Check back soon to see how others respond.'}
              </DS.Text>
            </DS.Frame>
            <DS.Frame gap="4">
              {recentResponses &&
                recentResponses.length > 0 &&
                recentResponses.map((data, index) => {
                  return (
                    <CardView
                      key={`card-view-${index}`}
                      name={data.userName}
                      message={data.response}
                      avatar={data.avatar}
                      avatarColor={USER_BADGE_COLOR[index % 2]}
                    />
                  )
                })}
            </DS.Frame>
            <DS.Frame style={recentResponses && recentResponses.length > 0 ? { marginTop: 18 } : { marginTop: 0 }}>
              {recentResponses.length > 0 && (
                <GradientButton
                  title="Read everyone’s responses"
                  onPress={() => {
                    onClose()
                    onPressViewResponse()
                  }}
                />
              )}
              <DS.Button
                variant={'Large/Primary'}
                type={'On Dark'}
                backgroundColor={'Brand/Cream'}
                borderColor={'Brand/Light Grey'}
                style={recentResponses.length > 0 ? { marginTop: 8 } : { marginTop: 0 }}
                onPress={() => {
                  onClose()
                }}
              >
                Close
              </DS.Button>
            </DS.Frame>
          </DS.Frame>
        </BottomSheetModal>
      </DS.Frame>
    )
  }
)

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F6F2E9',
    paddingHorizontal: 32
  },
  imageContainer: {
    paddingHorizontal: 12,
    backgroundColor: '#F6F2E9',
    alignItems: 'center'
  },
  image: {
    marginTop: -12,
    width: '100%'
  },
  bottomSheetHandleStyle: {
    backgroundColor: '#F6F2E9',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 0
  },
  bottomSheetIndicatorStyle: {
    display: 'none'
  },
  okayButton: {
    paddingHorizontal: DS.spacing[24],
    paddingVertical: 12.5,
    backgroundColor: DS.palette['Global/Black'],
    borderRadius: DS.spacing[24],
    width: '100%',
    height: 48
  },
  ignoreButton: {
    paddingHorizontal: DS.spacing[24],
    paddingVertical: 12.5,
    borderRadius: DS.spacing[24],
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    width: '100%',
    height: 48
  },
  actionsContainer: {
    flex: 1,
    paddingTop: 16,
    gap: 6,
    justifyContent: 'flex-start',
    alignItems: 'flex-start'
  },
  messageTopContainer: {
    alignItems: 'center',
    justifyContent: 'center'
  },
  discription: {
    marginBottom: 16
  },
  submitGradientButton: {
    // paddingHorizontal: DS.spacing[24],
    // paddingVertical: 12.5,
    borderRadius: DS.spacing[24],
    // borderWidth: 1,
    // borderColor: DS.palette['Brand/Light Grey'],
    width: '70%',
    height: 48
  },
  submitButton: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  cardContainer: {
    padding: 16,
    // height: 104,
    marginTop: 5,
    borderRadius: 12
  }
})
